import { useEffect, useState, useCallback } from "react";
import { leadsApi } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radar, Users, Loader2, Crown, ExternalLink } from "lucide-react";
import { relativeTime } from "@/lib/format";

const STATUSES = ["cold", "reach", "converted"];
const STATUS_STYLE = {
  cold: "border-sky-500/50 text-sky-300 bg-sky-500/10",
  reach: "border-amber-500/50 text-amber-300 bg-amber-500/10",
  converted: "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
};

export default function LeadConsole({ refreshKey }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [convening, setConvening] = useState(null);

  const load = useCallback(async () => {
    try { setLeads(await leadsApi.list()); }
    catch (e) { console.error("lead console load failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setStatus = async (lead, status) => {
    try {
      await leadsApi.setStatus(lead.id, status);
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`Lead marked ${status.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    }
  };

  const convene = async (lead) => {
    setConvening(lead.id);
    toast.info("Boardroom convening — all 7 agents brainstorming the Complete Package…");
    // leadsApi.huddle is idempotent (returns the existing awaiting huddle), so proxy
    // timeouts on long deliberations are recovered by retrying.
    let h = null;
    for (let attempt = 0; attempt < 3 && !h; attempt++) {
      try {
        h = await leadsApi.huddle(lead.id);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (detail) { toast.error(detail); break; }
        if (attempt < 2) {
          toast.info("Boardroom still deliberating — holding the line…");
          await new Promise((r) => setTimeout(r, 30000));
        } else {
          toast.error("Huddle did not report back — check the Action Hub in a minute; it may still land");
        }
      }
    }
    if (h) {
      toast.success(`BOARDROOM APPROVED — Complete Package ready for sign-off in the Action Hub (impact ${h.impact?.usd >= 0 ? "+" : ""}$${Math.round(h.impact?.usd || 0).toLocaleString()})`);
      load();
    }
    setConvening(null);
  };

  return (
    <section className="uc-card p-6 uc-fade" data-testid="lead-console">
      <div className="uc-chip mb-2"><Users size={11}/> LEAD CONSOLE · SCOUT PIPELINE</div>
      <h2 className="font-serif-display text-2xl">Every lead, tracked to conversion.</h2>
      <p className="text-white/50 text-xs mt-1 mb-4">
        Secondary leads found by the Scout plus captured inbound. One tap convenes all 7 agents
        on a Complete Package — Draft, ROI, Strategy — for your Executive Sign-off.
      </p>

      {loading && <div className="text-white/40 text-sm">Loading pipeline…</div>}
      {!loading && leads.length === 0 && (
        <div className="text-white/40 text-[11px] italic py-6 border border-dashed border-white/10 text-center">
          No leads yet — the Scout logs trigger-based secondary leads here after every intelligence sweep.
        </div>
      )}

      <div className="space-y-1.5 max-h-[420px] overflow-y-auto uc-scroll pr-1" data-testid="lead-console-list">
        {leads.map((l) => {
          const d = l.data || {};
          const name = d.company || d.name || d.email || l.id.slice(0, 8);
          const status = l.status || "cold";
          return (
            <div key={l.id} className="border border-white/10 hover:border-white/20 transition-colors px-3 py-2.5" data-testid={`lead-row-${l.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Radar size={12} className={l.source === "scout_intel" ? "text-cyan-300" : "text-white/40"}/>
                <span className="text-[13px] font-medium text-white/90" data-testid={`lead-name-${l.id}`}>{name}</span>
                <span className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border ${l.source === "scout_intel" ? "border-cyan-500/40 text-cyan-300" : "border-white/15 text-white/45"}`}>
                  {l.source === "scout_intel" ? `Scout · ${d.trigger_type || "trigger"}` : "Captured"}
                </span>
                {l.venture_name && (
                  <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-500/40 text-teal-300">{l.venture_name}</span>
                )}
                {d.source_url && (
                  <a href={d.source_url} target="_blank" rel="noreferrer" className="text-white/30 hover:text-amber-400" data-testid={`lead-source-${l.id}`}>
                    <ExternalLink size={10}/>
                  </a>
                )}
                <span className="ml-auto font-mono-uc text-[9px] text-white/30">{relativeTime(l.created_at)}</span>
              </div>
              {d.headline && <div className="text-[11px] text-white/55 mt-1 truncate" title={d.headline}>{d.headline}</div>}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <div className="flex items-center" data-testid={`lead-status-tracker-${l.id}`}>
                  {STATUSES.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`lead-status-${l.id}-${s}`}
                      aria-pressed={status === s}
                      onClick={() => s !== status && setStatus(l, s)}
                      className={`font-mono-uc text-[8px] uppercase tracking-widest px-2 py-1 border transition-colors ${i > 0 ? "-ml-px" : ""} ${status === s ? STATUS_STYLE[s] : "border-white/10 text-white/30 hover:text-white/60"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <Button
                  data-testid={`lead-huddle-btn-${l.id}`}
                  onClick={() => convene(l)}
                  disabled={convening === l.id}
                  className="uc-btn-amber rounded-none h-7 px-2.5 text-[9px] font-mono-uc uppercase tracking-wider ml-auto"
                >
                  {convening === l.id ? <Loader2 size={10} className="mr-1 animate-spin"/> : <Crown size={10} className="mr-1"/>}
                  Convene Huddle
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
